import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingContext } from '@/contexts/useOnboardingContext';
import { useAuth } from '@/contexts/useAuth';
import { updateProfile } from '@/lib/api/profile';
import { track } from '@/lib/analytics';
import type { FriendlyError } from '@/lib/errors';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import ErrorAlert from '@/components/ui/ErrorAlert';
import OnboardingShell from '@/components/layout/OnboardingShell';

export default function NamePage() {
  const navigate = useNavigate();
  const { user, refreshProfile } = useAuth();
  const { state, setName, setStep } = useOnboardingContext();
  const [name, setLocalName] = useState(state.full_name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<FriendlyError | null>(null);

  const handleContinue = async () => {
    const trimmed = name.trim();
    if (!trimmed || !user?.id) return;
    setSaving(true);
    setError(null);
    const result = await updateProfile(user.id, { full_name: trimmed });
    if (!result.success) {
      setError(result.error ?? null);
      setSaving(false);
      return;
    }
    setName(trimmed);
    setStep(2);
    await refreshProfile();
    track('onboarding_step_completed', { step: 'name' });
    navigate('/onboarding/interview-date');
  };

  return (
    <OnboardingShell
      step={1}
      title={<>What&apos;s your name?</>}
      subtitle={<>We&apos;d love to call you by name.</>}
    >
      <div className="space-y-6">
        <Input
          label="How should we call you?"
          placeholder="e.g. Sarah"
          value={name}
          onChange={setLocalName}
          maxLength={50}
          autoFocus
        />

        {error && <ErrorAlert error={error} />}

        <Button
          fullWidth
          onClick={handleContinue}
          disabled={!name.trim() || saving}
        >
          {saving ? 'Saving…' : 'Continue'}
        </Button>
      </div>
    </OnboardingShell>
  );
}
